import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { portfolioListPath } from '@/shared/config';
import { buttonVariants, cn } from '@/shared/ui';

/** 이전·다음 — 번호와 같은 36px 아웃라인 알약(스토어 페이지네이션과 같은 모양). */
const STEP_BUTTON_CLASS_NAME = buttonVariants({ variant: 'outline', size: 'icon' });

/** 번호 버튼 — 최소 36px 정사각 알약. 현재 페이지만 채운다. */
const PAGE_BUTTON_CLASS_NAME =
  'grid h-9 min-w-9 cursor-pointer place-items-center rounded-full px-2 text-sm tabular-nums transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface PortfolioPaginationProps {
  readonly categoryId?: string;
  readonly page: number;
  readonly totalPages: number;
}

/** 번호형 페이지네이션(‹ 1 2 3 ›) — 카테고리를 유지한 채 페이지 번호를 URL 에 쓴다. */
export const PortfolioPagination = ({
  categoryId,
  page,
  totalPages,
}: PortfolioPaginationProps): ReactElement | null => {
  const navigate = useNavigate();
  if (totalPages < 2) return null;
  const activePage = Math.min(Math.max(page, 1), totalPages);
  const first = Math.max(1, Math.min(activePage - 2, totalPages - 4));
  const pages = Array.from({ length: Math.min(5, totalPages) }, (_, index) => first + index);
  const go = (next: number) => {
    void navigate(portfolioListPath({ categoryId, page: next }), { replace: false });
  };
  return (
    <nav aria-label="포트폴리오 페이지" className="flex items-center justify-center gap-1">
      <button
        type="button"
        aria-label="이전 페이지"
        disabled={activePage === 1}
        onClick={() => go(activePage - 1)}
        className={STEP_BUTTON_CLASS_NAME}
      >
        <ChevronLeft aria-hidden className="size-4" strokeWidth={1.5} />
      </button>
      {pages.map((number) => (
        <button
          key={number}
          type="button"
          aria-current={number === activePage ? 'page' : undefined}
          onClick={() => go(number)}
          className={cn(
            PAGE_BUTTON_CLASS_NAME,
            number === activePage
              ? 'bg-accent font-semibold text-accent-foreground'
              : 'text-muted-foreground',
          )}
        >
          {number}
        </button>
      ))}
      <button
        type="button"
        aria-label="다음 페이지"
        disabled={activePage === totalPages}
        onClick={() => go(activePage + 1)}
        className={STEP_BUTTON_CLASS_NAME}
      >
        <ChevronRight aria-hidden className="size-4" strokeWidth={1.5} />
      </button>
    </nav>
  );
};
